import { onBeforeUnmount, onMounted, ref, shallowRef, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { onBeforeRouteUpdate } from 'vue-router'
import { useToastStore } from '../stores/useToastStore'

/**
 * A short action on a phone — approve, reject, remove, publish — runs **without a dialog and can
 * be taken back for five seconds** (docs/plans/mobile-navigation.md, "Moderation queue").
 *
 * Nothing is sent before the window closes: the request goes when the delay runs out, so taking a
 * gesture back needs no inverse operation on the server, which a "back to pending" would not have.
 * Until then the page reads `pending` to draw the row as if the gesture had landed.
 *
 * **One toast, the most recent gesture's.** Seven taps in a row would otherwise stack seven toasts
 * over the very rows being cleared. "Undo" takes back the most recent gesture still waiting; the
 * toast then shows the one before it, for what is left of its own five seconds, so every gesture
 * keeps its full window. Leaving the page sends whatever is waiting rather than dropping it.
 *
 * **A gesture is sent where it was made.** A request reads its surroundings as it goes — the
 * route's parameters, the organization in `X-Org-Id` —, so what waits is sent, and answered,
 * before they change: a route reused with other parameters waits for it in `beforeRouteUpdate`,
 * and the session guard waits for every queue (`landWaitingGestures`) before it switches the
 * organization. A tab put away or closed sends it too, which the client keeps alive.
 *
 * **The toast stands aside while a sheet or a dialog is open.** Toasts sit above every overlay
 * (MN5), and an undo toast at the thumb would cover a sheet's last lines and a full-screen
 * dialog's buttons; it comes back on close, for what is left of its window.
 */
export interface UndoableGesture<A extends string = string> {
  /** The row the gesture is about. A second gesture on the same row replaces the first. */
  key: string
  /** What the gesture does to the row, for the page to draw it as landed. */
  action: A
  /** What the toast says, already translated. */
  message: string
  /** The request, sent when the window closes. */
  commit: () => Promise<unknown>
  /** Said when the request fails; the row comes back as it was. */
  failure: string
}

interface Waiting<A extends string> extends UndoableGesture<A> {
  deadline: number
  timer: ReturnType<typeof setTimeout>
}

export const UNDO_WINDOW_MS = 5000

/** How long a navigation waits for the gestures it sends; a slower answer lands after it. */
const LANDING_WAIT_MS = 4000

// Every queue mounted, for the session guard to land them all before the organization changes.
const queues = new Set<() => Promise<void>>()

/** Sends every gesture still waiting, in every queue, and resolves once they are answered. */
export function landWaitingGestures(): Promise<void> {
  return waitAtMost(Promise.all([...queues].map((land) => land())).then(() => undefined))
}

function waitAtMost(landing: Promise<void>): Promise<void> {
  return Promise.race([landing, new Promise<void>((resolve) => setTimeout(resolve, LANDING_WAIT_MS))])
}

export function useUndoableQueue<A extends string = string>(windowMs = UNDO_WINDOW_MS) {
  const { t } = useI18n()
  const toasts = useToastStore()

  // Oldest first: the toast shows the last one, and "Undo" takes it off.
  const waiting = ref([]) as Ref<Waiting<A>[]>
  // Sent and not yet answered. No longer undoable, but still drawn as landed: the request
  // refetches the list before it resolves, and letting go earlier would flash the row back.
  const sending = new Map<string, A>()
  /** What each row is waiting for, by key: the page draws the row as if it had landed. */
  const pending = shallowRef<ReadonlyMap<string, A>>(new Map())
  let toastId: string | null = null
  /** The gesture the toast is about, so a gesture landing under it does not redraw it. */
  let toastFor: Waiting<A> | null = null
  let covered = false

  function redraw() {
    pending.value = new Map([
      ...sending,
      ...waiting.value.map((gesture) => [gesture.key, gesture.action] as const),
    ])
  }

  function sync() {
    redraw()
    watchOverlays(waiting.value.length > 0)
    const last = covered ? undefined : waiting.value.at(-1)
    if (last && last === toastFor) return
    if (toastId) toasts.remove(toastId)
    toastId = null
    toastFor = last ?? null
    if (!last) return
    toastId = toasts.add(last.message, {
      type: 'success',
      position: 'bottom-center',
      duration: Math.max(last.deadline - Date.now(), 0),
      closable: false,
      actions: [{ label: t('core.undo.action'), onClick: undoLast }],
    })
  }

  // Vuetify teleports every sheet and dialog into `.v-overlay-container`, which it creates with the
  // first one: only that subtree is watched, and the body's own children until it exists. Menus and
  // tooltips are not dialogs and do not hide the toast.
  const CONTAINER = '.v-overlay-container'
  const DIALOG_OPEN = `${CONTAINER} > .v-dialog.v-overlay--active`
  let observer: MutationObserver | null = null
  let watchingContainer = false

  function observe() {
    if (!observer) return
    const container = document.querySelector(CONTAINER)
    observer.disconnect()
    if (container) {
      observer.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] })
    } else {
      observer.observe(document.body, { childList: true })
    }
    watchingContainer = container !== null
  }

  function watchOverlays(on: boolean) {
    if (on && !observer) {
      covered = document.querySelector(DIALOG_OPEN) !== null
      observer = new MutationObserver(() => {
        if (!watchingContainer) observe()
        const now = document.querySelector(DIALOG_OPEN) !== null
        if (now === covered) return
        covered = now
        sync()
      })
      observe()
    } else if (!on && observer) {
      observer.disconnect()
      observer = null
      watchingContainer = false
      covered = false
    }
  }

  function take(key: string): Waiting<A> | undefined {
    const gesture = waiting.value.find((candidate) => candidate.key === key)
    if (!gesture) return undefined
    waiting.value = waiting.value.filter((candidate) => candidate !== gesture)
    clearTimeout(gesture.timer)
    return gesture
  }

  async function send(gesture: Waiting<A>) {
    sending.set(gesture.key, gesture.action)
    try {
      await gesture.commit()
    } catch {
      toasts.add(gesture.failure, { type: 'error', position: 'bottom-center' })
    } finally {
      // A newer gesture on the same row, run while this one was in flight, keeps its own entry.
      if (sending.get(gesture.key) === gesture.action) sending.delete(gesture.key)
      redraw()
    }
  }

  function land(key: string) {
    const gesture = take(key)
    if (gesture) void send(gesture)
    sync()
  }

  /** Draws the gesture as landed at once, and sends it when the window closes. */
  function run(gesture: UndoableGesture<A>) {
    take(gesture.key)
    const deadline = Date.now() + windowMs
    const timer = setTimeout(() => land(gesture.key), windowMs)
    waiting.value.push({ ...gesture, deadline, timer })
    sync()
  }

  /**
   * Takes back the row's gesture if it still waits, as "Undo" would; `false` once it has gone,
   * or when there was none.
   */
  function cancel(key: string): boolean {
    const gesture = take(key)
    if (gesture) sync()
    return gesture !== undefined
  }

  /** Takes back the most recent gesture still waiting. */
  function undoLast() {
    const last = waiting.value.at(-1)
    if (last) take(last.key)
    sync()
  }

  /**
   * Sends everything waiting now — the page is going, and a gesture is not dropped with it — and
   * resolves once every one is answered.
   */
  function flush(): Promise<void> {
    const all = [...waiting.value]
    for (const gesture of all) clearTimeout(gesture.timer)
    waiting.value = []
    const landing = Promise.all(all.map(send)).then(() => undefined)
    sync()
    return landing
  }

  // A tab put away may never come back to run the timers, and a closed one never does: both send
  // what waits, `visibilitychange` first since a closing tab is hidden before `pagehide`.
  function onHidden() {
    if (document.visibilityState === 'hidden') void flush()
  }
  function onPageHide() {
    void flush()
  }

  // The same component for other parameters — another workspace's queue — keeps its instance and
  // is not unmounted: what waits goes before the parameters it reads change. A query alone, a
  // filter, leaves it waiting.
  onBeforeRouteUpdate((to, from) =>
    JSON.stringify(to.params) === JSON.stringify(from.params) ? undefined : waitAtMost(flush()),
  )

  onMounted(() => {
    queues.add(flush)
    document.addEventListener('visibilitychange', onHidden)
    window.addEventListener('pagehide', onPageHide)
  })
  onBeforeUnmount(() => {
    queues.delete(flush)
    document.removeEventListener('visibilitychange', onHidden)
    window.removeEventListener('pagehide', onPageHide)
    void flush()
  })

  return { pending, run, cancel, undoLast, flush }
}
